import React from 'react';

const windowGlobal = typeof window !== 'undefined' && window;

interface IStorageOptions {
	rememberForever: boolean;
	useDefault?: boolean;
	useAndStoreDefault?: boolean;
}

const defaultStorageOptions: IStorageOptions = {
	rememberForever: false,		// We always store in session; we can also store in local storage if told to remember forever
	useDefault: false,			// Set to true to use default value as initial value instead of any stored value
	useAndStoreDefault: false,	// Set to true to use default and store it immediately to avoid render loops
};

export const useStateWithStorage = (itemKey: string, itemDefault: any, options?: IStorageOptions) => {
	if (!options) options = defaultStorageOptions;

	// Set initial value in state
	const [value, setValue] = React.useState(() => {
		// Use default value if requested
		if (options.useAndStoreDefault) {
			storeItem(itemKey, itemDefault, options.rememberForever);
			return itemDefault;
		}
		if (options.useDefault) return itemDefault;
		return getStoredItem(itemKey, itemDefault, options.rememberForever);
	});

	// Update stored value when value changed in state
	React.useEffect(() => {
		// Remove from store (or ignore) if new value is undefined or default
		if (value === undefined || value === itemDefault) {
			removeStoredItem(itemKey);
		}
		else {
			storeItem(itemKey, value, options.rememberForever);
		}
	}, [value, itemKey, itemDefault, options]);

	return [value, setValue];
};

// Use JSON.stringify and JSON.parse to preserve item types when storing, getting
const storeItem = (itemKey: string, itemValue: any, useLocalStorage: boolean) => {
	if (windowGlobal && windowGlobal.sessionStorage)
		windowGlobal.sessionStorage.setItem(itemKey, JSON.stringify(itemValue));
	if (windowGlobal && windowGlobal.localStorage && useLocalStorage) {
		windowGlobal.localStorage.setItem(itemKey, JSON.stringify(itemValue));
	}
	// Remove locally stored item if local storage no longer needed, but item currently saved there
	else if (windowGlobal && windowGlobal.localStorage) {
		windowGlobal.localStorage.removeItem(itemKey);
	}
};
const getStoredItem = (itemKey: string, itemDefault: any, useLocalStorage: boolean) => {
	// Always use session value if set, even if set to remember forever
	let storedValue = windowGlobal && windowGlobal.sessionStorage && windowGlobal.sessionStorage.getItem(itemKey);
	if (storedValue) return JSON.parse(storedValue);
	if (windowGlobal && windowGlobal.localStorage && useLocalStorage) {
		storedValue = windowGlobal.localStorage.getItem(itemKey);
		if (storedValue) return JSON.parse(storedValue);
	}
	return itemDefault;
};
const removeStoredItem = (itemKey: string) => {
	if (windowGlobal && windowGlobal.sessionStorage)
		windowGlobal.sessionStorage.removeItem(itemKey);
	if (windowGlobal && windowGlobal.localStorage)
		windowGlobal.localStorage.removeItem(itemKey);
};
